// Pure helpers shared by the visualization cards: chart suggestion from the
// output schema and row-to-chart-data mapping. Kept dependency-free so the
// logic is unit-testable without rendering. Theme helpers live in theme.js.

export const CHART_TYPES = [
  { key: 'map', label: 'Map' },
  { key: 'radar', label: 'Radar (orientations)' },
  { key: 'scatter', label: 'Scatter plot' },
  { key: 'line', label: 'Line chart' },
  { key: 'pie', label: 'Pie chart' },
  { key: 'bar', label: 'Bar chart' },
  { key: 'table', label: 'Table' }
];

// Two field shapes flow through the app: catalog fields carry `kind`
// (numeric/angle/enum/text/boolean/tags) while pipeline result fields carry
// `type` (number/text/boolean/list). Both are accepted.
function isNumeric(f) {
  return f.kind === 'numeric' || f.kind === 'angle' || f.type === 'number';
}

function isCategorical(f) {
  return ['enum', 'text', 'boolean'].includes(f.kind) || ['text', 'boolean'].includes(f.type);
}

export function numericFields(fields) {
  return (fields || []).filter(isNumeric);
}

export function categoricalFields(fields) {
  return (fields || []).filter(isCategorical);
}

// Geographic fields are named latitude/longitude, optionally prefixed by the
// join that provides them (e.g. site_latitude). The latitude and longitude
// must come from the same prefix.
function geoPrefix(fields, base) {
  const keys = (fields || []).map((f) => f.key);
  if (keys.includes(base)) return null;
  const suffix = `_${base}`;
  const prefixed = keys.find((k) => k.endsWith(suffix));
  return prefixed === undefined ? undefined : prefixed.slice(0, -suffix.length);
}

export function geoField(fields, base) {
  const prefix = geoPrefix(fields, base);
  if (prefix === undefined) return undefined;
  const key = prefix ? `${prefix}_${base}` : base;
  return (fields || []).find((f) => f.key === key);
}

export function hasGeoFields(fields) {
  const lat = geoPrefix(fields, 'latitude');
  const lon = geoPrefix(fields, 'longitude');
  return lat !== undefined && lon !== undefined && lat === lon;
}

// A chart is offered only when the output schema can feed it.
export function availableCharts(fields) {
  const numeric = numericFields(fields);
  const categorical = categoricalFields(fields);
  const charts = ['table'];
  if (hasGeoFields(fields)) charts.push('map');
  if (categorical.length > 0) charts.push('radar', 'pie', 'bar');
  if (numeric.length >= 2) charts.push('scatter', 'line');
  return charts;
}

// A sensible default for the x (category) axis: enums first, then the first
// categorical that is not an identifier (identifiers have too much cardinality
// for a slice).
function defaultXField(categorical) {
  const enumField = (categorical || []).find((f) => f.kind === 'enum');
  if (enumField) return enumField.key;
  const nonIdentifier = (categorical || []).find((f) => f.key !== 'identifier');
  if (nonIdentifier) return nonIdentifier.key;
  return (categorical || [])[0] ? categorical[0].key : null;
}

export function defaultConfigFor(chartKey, fields) {
  const numeric = numericFields(fields);
  const categorical = categoricalFields(fields);
  const firstNumeric = numeric[0] ? numeric[0].key : null;
  const xField = defaultXField(categorical);
  // The `series` workflow node tags rows with a `series` column; charts
  // default to splitting on it when it exists.
  const seriesField = (fields || []).some((f) => f.key === 'series') ? 'series' : null;

  switch (chartKey) {
    case 'map':
      return { baseLayer: 'osm', layers: [] };
    case 'heatmap':
    case 'matrix':
      return {};
    case 'scatter':
      return {
        x: numeric[0] ? numeric[0].key : null,
        y: numeric[1] ? numeric[1].key : null,
        color: seriesField || xField
      };
    case 'line':
      return {
        x: xField || (numeric[0] ? numeric[0].key : null),
        y: numeric[numeric.length - 1] ? numeric[numeric.length - 1].key : null,
        seriesField
      };
    case 'pie':
    case 'bar':
    case 'radar':
      return { x: xField, y: firstNumeric, seriesField };
    default:
      return {};
  }
}

export {
  prepareCategories,
  prepareSeries,
  preparePoints,
  prepareLine,
  prepareLineSeries,
  prepareStacked,
  clusterParts,
  donutParts,
  aggregateValues
} from './prepare';
export { displayLabel, schemaLabel } from './labels';
